<template>
  <div class="p-[24px] rounded-lg border-[1px] border-[#e2e2e2] container">
    <div class="flex items-center justify-between flex-wrap gap-10 mb-[24px]">
      <h3 class="text-[16px] font-semibold text-[#333333] mb-0">
        Grafik Tren Request
      </h3>
      <b-overlay
        :show="loading"
        spinner-variant="primary"
        variant="white"
        opacity=".5"
        spinner-small
        class="flex items-center justify-end gap-[10px] flex-wrap"
      >
        <date-picker
          v-model="period"
          :date-format="createDatePickerRestrictor()"
          :ranges="datePickerRangeGeneral"
        >
          <template #default="{data: dataPeriod}">
            <div class="d-flex justify-between gap-[14px] items-center w-100 p-[3px]">
              <span class="text-[14px] text-[#c2c2c2]">
                {{ getFormattedDateRange(dataPeriod.startDate, dataPeriod.endDate) }}
              </span>
              <span class="k-calendar text-[24px]" />
            </div>
          </template>
        </date-picker>
      </b-overlay>
    </div>
    <b-overlay
      :show="loadingFirstData"
      spinner-variant="primary"
      variant="white"
      opacity="1"
      class="col-span-2"
    >
      <div class="grid grid-cols-1 sm:grid-cols-3 gap-0 sm:gap-10">

        <apex-chart
          class="col-span-2"
          :options="generateOptions"
          :series="series"
          height="450"
        />
        <div class="mt-[50px] w-full">
          <tren-card
            id="reqpackingInfo"
            color="#FBA63C"
            title="Request Packing"
            popover-content="Jumlah data request packing terhadap mitra"
            :content="data.total_packing_requests || 0"
            :link="requestPackingLink"
          />
          <tren-card
            id="penarikanbarangInfo"
            color="#08A0F7"
            title="Penarikan Barang"
            popover-content="Jumlah request penarikan barang yang dilakukan partner terhadap mitra"
            :content="data.Total_item_withdrawal || 0"
            :link="requestPenarikanBarangLink"
          />
          <tren-card
            id="barangrusakInfo"
            color="#E31A1A"
            title="Barang Rusak"
            popover-content="Jumlah request pengeluaran barang rusak yang dilakukan mitra"
            :content="data.Total_item_damaged || 0"
            :link="requestBarangRusakLink"
          />
        </div>
      </div>
    </b-overlay>
  </div>
</template>

<script>
import { DAY_MONTH_YEAR, YEAR_MONTH_DAY, createDatePickerRestrictor } from '@/libs/filterDate'
import {
  firstDateOfMonth, lastDateOfMonth, generateDateRange, today, last30,
} from '@/store/helpers'
import ApexChart from 'vue-apexcharts'
import DatePicker from '../DatePicker.vue'
import { trenRequestOptionsChart } from './config'
import { datePickerRangeGeneral } from '../config'
import { getFormattedDateRange } from '../helpers'
import TrenCard from './TrenCard.vue'

export default {
  components: {
    DatePicker,
    ApexChart,
    TrenCard,
  },
  data() {
    return {
      loadingFirstData: true,
      loading: true,
      data: {
        total_packing_requests: 0,
        Total_item_damaged: 0,
        Total_item_withdrawal: 0,
      },
      period: {
        startDate: this.$route.query.gtes ? new Date(this.$route.query.gtes) : last30,
        endDate: this.$route.query.gtee ? new Date(this.$route.query.gtee) : today,
      },
      series: [
        {
          name: 'Request Packing',
          data: [],
        },
        {
          name: 'Penarikan Barang',
          data: [],
        },
        {
          name: 'Barang Rusak',
          data: [],
        },
      ],
      categories: generateDateRange(firstDateOfMonth, lastDateOfMonth, date => YEAR_MONTH_DAY(date)),
    }
  },
  computed: {
    generateOptions() {
      return {
        ...trenRequestOptionsChart,
        xaxis: {
          type: 'datetime',
          categories: this.categories,
          tooltip: {
            formatter(value) {
              return DAY_MONTH_YEAR(value)
            },
          },
          labels: {
            format: 'dd',
          },
        },
      }
    },
    requestPackingLink() {
      return `/kompack/total-nilai-transaksi?start_date=${YEAR_MONTH_DAY(this.period.startDate)}&end_date=${YEAR_MONTH_DAY(this.period.endDate)}&card=tbf`
    },
    requestPenarikanBarangLink() {
      return `/kompack/total-nilai-transaksi?start_date=${YEAR_MONTH_DAY(this.period.startDate)}&end_date=${YEAR_MONTH_DAY(this.period.endDate)}&card=tbp&tab=withdrawal`
    },
    requestBarangRusakLink() {
      return `/kompack/total-nilai-transaksi?start_date=${YEAR_MONTH_DAY(this.period.startDate)}&end_date=${YEAR_MONTH_DAY(this.period.endDate)}&card=tbp&tab=damaged`
    },
  },
  watch: {
    period() {
      this.getData()
      this.categories = generateDateRange(this.period.startDate, this.period.endDate, date => YEAR_MONTH_DAY(date))
      this.updatePeriodQuery()
    },
  },
  created() {
    this.getData()
    this.datePickerRangeGeneral = datePickerRangeGeneral(today)
  },
  methods: {
    getFormattedDateRange,
    createDatePickerRestrictor,
    DAY_MONTH_YEAR,
    updatePeriodQuery() {
      this.$router.push({
        query: {
          ...this.$route.query,
          gtes: YEAR_MONTH_DAY(this.period.startDate),
          gtee: YEAR_MONTH_DAY(this.period.endDate),
        },
      })
    },
    /**
     * Resets all data properties to their initial values
     */
    resetData() {
      this.data = {
        total_packing_requests: 0,
        total_item_damaged: 0,
        total_item_withdrawal: 0,
        list_packing_requests: {},
        list_item_withdrawal: {},
        list_item_damaged: {},
      }
      this.series = [
        { name: 'Request Packing', data: [] },
        { name: 'Penarikan Barang', data: [] },
        { name: 'Barang Rusak', data: [] },
      ]
    },

    /**
     * Fetches trend request data from API and updates the chart
     * @returns {Promise<void>}
     */
    getData() {
      this.loading = true
      const url = '/kompack/api/v1/dashboard/admin/trend-requests'
      const dataSeries = [
        {
          name: 'Request Packing',
          data: [],
        },
        {
          name: 'Penarikan Barang',
          data: [],
        },
        {
          name: 'Barang Rusak',
          data: [],
        },
      ]
      const keys = ['list_packing_requests', 'list_item_withdrawal', 'list_item_damaged']
      this.$http_new.get(url, {
        params: {
          start_date: YEAR_MONTH_DAY(this.period.startDate),
          end_date: YEAR_MONTH_DAY(this.period.endDate),
        },
      }).then(res => {
        const { data } = res.data
        this.data = data

        this.categories.forEach(date => {
          keys.forEach((key, index) => {
            const value = data[key]?.[date] ?? 0
            dataSeries[index].data.push(value)
          })
        })

        this.series = dataSeries
      }).catch(err => {
        this.$toast_error({ message: err.status === 404 ? 'Data tidak ditemukan' : 'Gagal mengambil data, silahkan coba lagi.' })
        this.data = {
          total_packing_requests: 0,
          total_item_damaged: 0,
          total_item_withdrawal: 0,
        }
        this.categories.forEach(date => {
          keys.forEach((_key, index) => {
            dataSeries[index].data.push(0)
          })
        })

        this.series = dataSeries
      }).finally(() => {
        this.loading = false
        if (this.loadingFirstData) {
          this.loadingFirstData = false
        }
      })
    },
  },
}
</script>
